<script lang="ts">
export default {
  name: 'MonitoringPage',
};
</script>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { Award, Download, Info, Loader, Target } from '@lucide/vue';
import { aiService } from '@/services/AIService';
import { policyTypeService } from '@/services/PolicyTypeService';
import type {
  BinderValidationStatus,
  ModelPerformance,
  PromptAccuracy,
  UnvalidatedBindersByCoverageType,
} from '@/types/ai';
import type { CoverageType } from '@/types/PolicyType';
import type { FusionChartDoughnut2D } from '@/types/Doughnut';
import type { FusionChartBar2D } from '@/types/Bar';
import type { FusionChartMSCombiDY2D } from '@/types/MultiSeries';
import { chartPrimaryColor } from '@/utilities/Color';
import { DownloadCSV } from '@/utilities/Download';
import GenericMultiFilter from '@/components/v2/GenericMultiFilter.vue';

const loading = ref(true);
const error = ref<string | null>(null);
const status = ref<BinderValidationStatus | null>(null);

const coverageLoading = ref(true);
const coverageError = ref<string | null>(null);
const coverageRows = ref<UnvalidatedBindersByCoverageType[]>([]);

const coverageTypes = ref<CoverageType[]>([]);
const selectedCoverageTypeIds = ref<(string | number)[]>([]);

const modelLoading = ref(true);
const modelError = ref<string | null>(null);
const modelRows = ref<ModelPerformance[]>([]);
// 0 means all time
const modelRangeDays = ref(30);

const accuracyLoading = ref(true);
const accuracyError = ref<string | null>(null);
const accuracyRows = ref<PromptAccuracy[]>([]);
// 0 means all time
const accuracyRangeDays = ref(30);

const coverageTypeOptions = computed(() =>
  coverageTypes.value.map((ct) => ({ label: ct.coverageType, value: ct.coverageTypeId })),
);

const chartDataSource = computed<FusionChartDoughnut2D | null>(() => {
  if (!status.value) return null;

  return {
    chart: {
      caption: 'Binders Validation Status',
      defaultCenterLabel: `Total{br}<b>${status.value.totalBinders.toLocaleString()}</b>`,
      centerLabel: '$label: $value',
      centerLabelFontSize: 14,
      showLegend: false,
      paletteColors: ['#22c55e', '#f0b001', '#697282', '#ef4444'],
      decimals: 0,
      theme: 'fusion',
      doughnutRadius: '90',
      chartTopMargin: 10,
      chartBottomMargin: 10,
      showBorder: false,
    },
    data: [
      { label: 'Validated', value: status.value.validatedCount },
      { label: 'Unvalidated', value: status.value.unvalidatedCount },
      { label: 'Unprocessed', value: status.value.unprocessedCount },
      { label: 'Exceptions', value: status.value.exceptionCount },
    ],
  };
});

const coverageChartDataSource = computed<FusionChartBar2D>(() => ({
  chart: {
    caption: 'Unvalidated Binders by Coverage Type',
    captionAlignment: 'center',
    captionFontBold: '0',
    animation: '1',
    theme: 'cacs',
    showValues: true,
    paletteColors: [chartPrimaryColor],
    decimals: 0,
    labelDisplay: 'auto',
    useEllipsesWhenOverflow: '1',
    xAxisName: 'Coverage Type',
    yAxisName: 'Unvalidated Binders',
  },
  data: coverageRows.value.slice(0, 10).map((row) => ({
    label: row.coverageTypeName,
    value: row.count,
  })),
}));

const modelChips = computed(() => {
  const totalRuns = modelRows.value.reduce((sum, r) => sum + r.runs, 0);
  const totalFields = modelRows.value.reduce((sum, r) => sum + r.totalFieldsCaptured, 0);
  const reviewedRuns = modelRows.value.reduce((sum, r) => sum + r.reviewedRuns, 0);

  return [
    {
      key: 'model-total-extractions',
      value: totalRuns.toLocaleString(),
      label: 'total extractions',
      color: 'text-blue-700',
    },
    {
      key: 'model-models-active',
      value: modelRows.value.length.toLocaleString(),
      label: 'models active',
      color: 'text-slate-900',
    },
    {
      key: 'model-avg-fields-captured',
      value: totalRuns ? (totalFields / totalRuns).toFixed(1) : '--',
      label: 'avg fields captured / run',
      color: 'text-teal-600',
    },
    {
      key: 'model-pct-reviewed',
      value: totalRuns ? `${((100 * reviewedRuns) / totalRuns).toFixed(1)}%` : '--',
      label: 'results reviewed',
      color: 'text-blue-700',
    },
  ];
});

const lastExtractionLabel = computed(() => {
  // lastRunAt strings are ISO-shaped, so lexicographic max is the latest
  const latest = modelRows.value.reduce<string | null>(
    (max, r) => (max && max >= r.lastRunAt ? max : r.lastRunAt),
    null,
  );
  if (!latest) return null;
  return new Date(latest).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
});

const modelChartDataSource = computed<FusionChartMSCombiDY2D>(() => ({
  chart: {
    animation: '1',
    theme: 'cacs',
    showValues: false,
    showLegend: true,
    legendPosition: 'top',
    paletteColors: ['#60a5fa', '#4ade80'],
    decimals: 1,
    labelDisplay: 'auto',
    useEllipsesWhenOverflow: '1',
    pYAxisName: 'Avg fields captured',
    sYAxisName: 'Avg reviews',
  },
  categories: [{ category: modelRows.value.map((row) => ({ label: row.modelName })) }],
  dataset: [
    {
      seriesname: 'Avg fields captured per run',
      data: modelRows.value.map((row) => ({ value: row.avgFieldsPerRun })),
    },
    {
      seriesname: 'Avg reviews per result',
      parentyaxis: 'S',
      renderas: 'column',
      data: modelRows.value.map((row) => ({ value: row.avgReviewsPerRun })),
    },
  ],
}));

async function fetchValidationStatus() {
  loading.value = true;
  error.value = null;
  try {
    const ids = selectedCoverageTypeIds.value.map((v) => Number(v));
    status.value = await aiService.getBinderValidationStatus(ids.length ? ids : undefined);
  } catch (e: unknown) {
    error.value = e instanceof Error ? e.message : 'Failed to load binder validation status';
  } finally {
    loading.value = false;
  }
}

async function fetchCoverageTypes() {
  try {
    coverageTypes.value = (await policyTypeService.GetDistinctCoverageTypes()) ?? [];
  } catch {
    coverageTypes.value = [];
  }
}

async function fetchCoverageBreakdown() {
  coverageLoading.value = true;
  coverageError.value = null;
  try {
    coverageRows.value = await aiService.getUnvalidatedBindersByCoverageType();
  } catch (e: unknown) {
    coverageError.value =
      e instanceof Error ? e.message : 'Failed to load unvalidated binders by coverage type';
  } finally {
    coverageLoading.value = false;
  }
}

function promptLabel(row: PromptAccuracy) {
  return row.promptVersion ? `${row.promptName} v${row.promptVersion}` : row.promptName;
}

// Rows with accuracy null (no reviewed data, e.g. malformed-only) are kept out
// of the chart so they read as "no data" rather than 0%; the table still shows them.
const accuracyChartRows = computed(() => accuracyRows.value.filter((r) => r.accuracy !== null));

const accuracyChartDataSource = computed<FusionChartBar2D>(() => ({
  chart: {
    animation: '1',
    theme: 'cacs',
    showValues: true,
    paletteColors: [chartPrimaryColor],
    decimals: 1,
    labelDisplay: 'auto',
    useEllipsesWhenOverflow: '1',
    xAxisName: 'Prompt',
    yAxisName: 'Accuracy (%)',
  },
  data: accuracyChartRows.value.map((row) => ({
    label: promptLabel(row),
    value: row.accuracy ?? 0,
    displayValue: `${row.accuracy}%`,
    toolText:
      `${promptLabel(row)}{br}` +
      `Accuracy: ${row.accuracy}%{br}` +
      `Reviewed extractions: ${row.reviewedResults}{br}` +
      `Fields evaluated: ${row.evaluatedFields}{br}` +
      `Confirmed: ${row.confirmedFields} &middot; Edited: ${row.editedFields}{br}` +
      `Malformed results: ${row.malformedResults}`,
  })),
}));

const lastReviewLabel = computed(() => {
  // lastReviewedAt strings are ISO-shaped, so lexicographic max is the latest
  const latest = accuracyRows.value.reduce<string | null>(
    (max, r) => (r.lastReviewedAt && (!max || r.lastReviewedAt > max) ? r.lastReviewedAt : max),
    null,
  );
  if (!latest) return null;
  return new Date(latest).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
});

async function fetchModelPerformance() {
  modelLoading.value = true;
  modelError.value = null;
  try {
    modelRows.value = await aiService.getModelPerformance(modelRangeDays.value || undefined);
  } catch (e: unknown) {
    modelError.value = e instanceof Error ? e.message : 'Failed to load model performance';
  } finally {
    modelLoading.value = false;
  }
}

function exportModelPerformance() {
  const headers = [
    'Model',
    'ARN',
    'Runs',
    'Reviewed Runs',
    'Total Reviews',
    'Avg Reviews/Run',
    'Fields Captured',
    'Avg Fields/Run',
    'Last Run',
  ];
  const lines = modelRows.value.map((r) =>
    [
      r.modelName,
      r.modelArn,
      r.runs,
      r.reviewedRuns,
      r.totalReviews,
      r.avgReviewsPerRun,
      r.totalFieldsCaptured,
      r.avgFieldsPerRun,
      r.lastRunAt,
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(','),
  );
  DownloadCSV([headers.join(','), ...lines].join('\n'), 'model-performance');
}

async function fetchPromptAccuracy() {
  accuracyLoading.value = true;
  accuracyError.value = null;
  try {
    accuracyRows.value = await aiService.getPromptAccuracy(accuracyRangeDays.value || undefined);
  } catch (e: unknown) {
    accuracyError.value = e instanceof Error ? e.message : 'Failed to load prompt accuracy';
  } finally {
    accuracyLoading.value = false;
  }
}

function exportPromptAccuracy() {
  const headers = [
    'Prompt',
    'Version',
    'Accuracy %',
    'Reviewed Results',
    'Evaluated Fields',
    'Confirmed',
    'Edited',
    'Malformed Results',
    'Last Reviewed',
  ];
  const lines = accuracyRows.value.map((r) =>
    [
      r.promptName,
      r.promptVersion ?? '',
      r.accuracy ?? '',
      r.reviewedResults,
      r.evaluatedFields,
      r.confirmedFields,
      r.editedFields,
      r.malformedResults,
      r.lastReviewedAt ?? '',
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(','),
  );
  DownloadCSV([headers.join(','), ...lines].join('\n'), 'prompt-accuracy');
}

onMounted(() => {
  fetchValidationStatus();
  fetchCoverageBreakdown();
  fetchCoverageTypes();
  fetchModelPerformance();
  fetchPromptAccuracy();
});
</script>

<template>
  <div>
    <h1 class="pl-2 text-2xl" data-test="title">Monitoring</h1>

    <main class="auto-grid graphs">
      <div class="chart-cell" data-test="binder-validation-status-chart">
        <div class="mb-2 flex justify-end">
          <GenericMultiFilter
            v-model="selectedCoverageTypeIds"
            :options="coverageTypeOptions"
            title="Coverage Type"
            chip-title-field="label"
            data-test="binder-validation-coverage-type-filter"
            @update:model-value="fetchValidationStatus" />
        </div>

        <div v-if="loading" class="flex items-center justify-center py-16">
          <Loader class="h-8 w-8 animate-spin text-blue-600" />
        </div>

        <div v-else-if="error" class="py-8 text-center text-red-600">
          {{ error }}
        </div>

        <div v-else-if="chartDataSource">
          <fusioncharts
            type="doughnut2d"
            width="100%"
            height="400"
            dataFormat="json"
            :dataSource="chartDataSource" />

          <div class="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <div class="flex items-center gap-2">
              <span class="inline-block h-3 w-3 rounded-full bg-green-500"></span>
              <span class="text-sm text-slate-700">Validated ({{ status?.validatedCount }})</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="inline-block h-3 w-3 rounded-full bg-yellow-500"></span>
              <span class="text-sm text-slate-700">Unvalidated ({{ status?.unvalidatedCount }})</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="inline-block h-3 w-3 rounded-full bg-gray-500"></span>
              <span class="text-sm text-slate-700">Unprocessed ({{ status?.unprocessedCount }})</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="inline-block h-3 w-3 rounded-full bg-red-500"></span>
              <span class="text-sm text-slate-700">Exceptions ({{ status?.exceptionCount }})</span>
            </div>
          </div>
        </div>
      </div>

      <div class="chart-cell" data-test="unvalidated-binders-by-coverage-type-chart">
        <div class="mb-2 flex justify-end" aria-hidden="true">
          <GenericMultiFilter
            :options="[]"
            :model-value="[]"
            title="Coverage Type"
            class="invisible" />
        </div>

        <div v-if="coverageLoading" class="flex items-center justify-center py-16">
          <Loader class="h-8 w-8 animate-spin text-blue-600" />
        </div>

        <div v-else-if="coverageError" class="py-8 text-center text-red-600">
          {{ coverageError }}
        </div>

        <div v-else-if="coverageRows.length === 0" class="py-8 text-center text-sm text-slate-600">
          No unvalidated binders found.
        </div>

        <fusioncharts
          v-else
          type="bar2d"
          width="100%"
          height="400"
          dataFormat="json"
          :dataSource="coverageChartDataSource" />
      </div>
    </main>

    <section class="mx-auto mt-10 max-w-5xl px-2" data-test="model-performance-section">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 class="flex items-center gap-2 text-lg font-semibold text-blue-800">
            <Award class="h-5 w-5" />
            Performance by Model
          </h2>
          <p class="mt-1 text-sm text-slate-500">
            Source: vw_AIExtractionResults<template v-if="lastExtractionLabel">
              &middot; Last extraction {{ lastExtractionLabel }}</template
            >
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <select
            v-model.number="modelRangeDays"
            class="select select-bordered select-sm"
            data-test="model-performance-range"
            @change="fetchModelPerformance">
            <option :value="30">Last 30 days</option>
            <option :value="90">Last 90 days</option>
            <option :value="0">All time</option>
          </select>
          <button
            class="btn btn-outline btn-sm border-slate-300 font-normal normal-case text-slate-700"
            :disabled="modelLoading || modelRows.length === 0"
            data-test="model-performance-export"
            @click="exportModelPerformance">
            <Download class="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      <div v-if="modelError" class="py-8 text-center text-red-600">
        {{ modelError }}
      </div>

      <div v-else-if="modelLoading" class="flex items-center justify-center py-16">
        <Loader class="h-8 w-8 animate-spin text-blue-600" />
      </div>

      <template v-else>
        <div class="mt-4 flex flex-wrap gap-3" data-test="model-performance-chips">
          <div
            v-for="chip in modelChips"
            :key="chip.key"
            class="flex items-baseline gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 shadow-sm">
            <span class="text-xl font-bold" :class="chip.color">{{ chip.value }}</span>
            <span class="text-sm text-slate-500">{{ chip.label }}</span>
          </div>
        </div>

        <div v-if="modelRows.length === 0" class="py-8 text-center text-sm text-slate-600">
          No extraction results found{{ modelRangeDays ? ' for the selected time range' : '' }}.
        </div>

        <template v-else>
          <div
            class="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            data-test="model-performance-chart">
            <fusioncharts
              type="mscombidy2d"
              width="100%"
              height="380"
              dataFormat="json"
              :dataSource="modelChartDataSource" />
          </div>

          <div
            class="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
            data-test="model-performance-table">
            <table class="table w-full text-sm">
              <thead>
                <tr class="text-xs uppercase tracking-wider text-slate-500">
                  <th class="text-left">Model</th>
                  <th class="text-right">Runs</th>
                  <th class="text-right">Total Reviews</th>
                  <th class="text-right">Fields Captured</th>
                  <th class="text-right">Avg Fields / Run</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in modelRows" :key="row.modelArn">
                  <td class="text-left">
                    <div class="font-semibold text-slate-900">{{ row.modelName }}</div>
                    <div class="font-mono text-xs text-slate-400">{{ row.modelArn }}</div>
                  </td>
                  <td class="text-right">{{ row.runs.toLocaleString() }}</td>
                  <td class="text-right">
                    {{ row.totalReviews.toLocaleString() }}
                    <span class="ml-1 text-xs text-slate-400"
                      >{{ row.avgReviewsPerRun.toFixed(1) }} avg</span
                    >
                  </td>
                  <td class="text-right">{{ row.totalFieldsCaptured.toLocaleString() }}</td>
                  <td class="text-right font-semibold">{{ row.avgFieldsPerRun.toFixed(1) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Info class="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Fields captured = count of distinct fields indexed per extraction run
              (AIPromptResultFieldOverride). Reviews = number of times a human validated the result
              (AIPromptResultReview).
            </span>
          </p>
        </template>
      </template>
    </section>

    <section class="mx-auto mt-10 max-w-5xl px-2" data-test="prompt-accuracy-section">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 class="flex items-center gap-2 text-lg font-semibold text-blue-800">
            <Target class="h-5 w-5" />
            Average Accuracy by Prompt
          </h2>
          <p class="mt-1 text-sm text-slate-500">
            Source: field-level review decisions (AIPromptResultFieldReview)<template
              v-if="lastReviewLabel">
              &middot; Last review {{ lastReviewLabel }}</template
            >
          </p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <select
            v-model.number="accuracyRangeDays"
            class="select select-bordered select-sm"
            data-test="prompt-accuracy-range"
            @change="fetchPromptAccuracy">
            <option :value="30">Last 30 days</option>
            <option :value="90">Last 90 days</option>
            <option :value="0">All time</option>
          </select>
          <button
            class="btn btn-outline btn-sm border-slate-300 font-normal normal-case text-slate-700"
            :disabled="accuracyLoading || accuracyRows.length === 0"
            data-test="prompt-accuracy-export"
            @click="exportPromptAccuracy">
            <Download class="h-4 w-4" />
            Export
          </button>
        </div>
      </div>

      <div v-if="accuracyError" class="py-8 text-center text-red-600">
        {{ accuracyError }}
      </div>

      <div v-else-if="accuracyLoading" class="flex items-center justify-center py-16">
        <Loader class="h-8 w-8 animate-spin text-blue-600" />
      </div>

      <template v-else>
        <div v-if="accuracyRows.length === 0" class="py-8 text-center text-sm text-slate-600">
          No reviewed extractions found{{ accuracyRangeDays ? ' for the selected time range' : '' }}.
        </div>

        <template v-else>
          <div
            v-if="accuracyChartRows.length > 0"
            class="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            data-test="prompt-accuracy-chart">
            <fusioncharts
              type="bar2d"
              width="100%"
              height="380"
              dataFormat="json"
              :dataSource="accuracyChartDataSource" />
          </div>

          <div
            class="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm"
            data-test="prompt-accuracy-table">
            <table class="table w-full text-sm">
              <thead>
                <tr class="text-xs uppercase tracking-wider text-slate-500">
                  <th class="text-left">Prompt</th>
                  <th class="text-right">Accuracy</th>
                  <th class="text-right">Reviewed</th>
                  <th class="text-right">Fields Evaluated</th>
                  <th class="text-right">Confirmed / Edited</th>
                  <th class="text-right">Malformed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in accuracyRows" :key="`${row.promptId}-${row.promptVersion}`">
                  <td class="text-left">
                    <div class="font-semibold text-slate-900">{{ row.promptName }}</div>
                    <div class="font-mono text-xs text-slate-400">
                      {{ row.promptVersion ? `v${row.promptVersion}` : 'no version' }}
                    </div>
                  </td>
                  <td class="text-right font-semibold">
                    {{ row.accuracy !== null ? `${row.accuracy.toFixed(1)}%` : '—' }}
                  </td>
                  <td class="text-right">{{ row.reviewedResults.toLocaleString() }}</td>
                  <td class="text-right">{{ row.evaluatedFields.toLocaleString() }}</td>
                  <td class="text-right">
                    {{ row.confirmedFields.toLocaleString() }} /
                    {{ row.editedFields.toLocaleString() }}
                  </td>
                  <td class="text-right">{{ row.malformedResults.toLocaleString() }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="mt-3 flex items-start gap-2 text-xs text-slate-500">
            <Info class="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Accuracy = confirmed fields / (confirmed + edited) from each result's first completed
              review; excluded fields count as edited. Malformed results are excluded from accuracy
              and counted separately. Prompts with no reviewed data show &mdash; instead of 0%.
            </span>
          </p>
        </template>
      </template>
    </section>
  </div>
</template>

<style scoped>
.auto-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  align-items: start;
  grid-gap: 1rem;
  margin-top: 1rem;
}

.chart-cell {
  margin: 0;
}
</style>
